import {
  Box3,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  Euler,
  FrontSide,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix3,
  Matrix4,
  PlaneGeometry,
  ShaderMaterial,
  Sphere,
  Texture,
  Vector3,
} from 'three';

export type ParticleEmitterOrientation = 'billboard' | 'billboardY' | 'fixed' | 'velocity';

// Flipbook animation options
export interface ParticleEmitterFlipbookOptions {
  columns: number;
  rows: number;
  fps?: number;
  loop?: boolean;
}

// Internal flipbook state
export interface ParticleEmitterFlipbookState {
  columns: number;
  rows: number;
  frameCount: number;
  fps: number;
  loop: boolean;
}

// `lifetime: 0` is treated as immortal particles.
// Use large finite values instead of Infinity to avoid GPU/CPU edge cases.
const IMMORTAL_PARTICLE_MAX_LIFE = 3.0e38;
const IMMORTAL_PARTICLE_BOUNDS_MAX_LIFE = 1.0e6;

// Helper function to validate flipbook options
export function isValidFlipbook(options: ParticleEmitterFlipbookOptions | undefined): options is ParticleEmitterFlipbookOptions {
  const isValidFps = options?.fps === undefined || (Number.isFinite(options.fps) && options.fps > 0);
  return options !== undefined && options.columns > 0 && options.rows > 0 && isValidFps;
}

// Convert options to state with defaults
export function toFlipbookState(options: ParticleEmitterFlipbookOptions): ParticleEmitterFlipbookState {
  return {
    columns: options.columns,
    rows: options.rows,
    frameCount: options.columns * options.rows,
    fps: options.fps ?? 30,
    loop: options.loop ?? true,
  };
}

// Cap lifetime for non-looping flipbook
export function capLifetimeIfNonLooping(lifetime: number, flipbook: ParticleEmitterFlipbookState | null): number {
  if (!flipbook || flipbook.loop) return lifetime;
  const animDuration = flipbook.frameCount / flipbook.fps;
  return Math.min(lifetime, animDuration);
}

// Resolve runtime particle lifetime
export function resolveRuntimeParticleLifetime(
  baseLifetime: number,
  lifetimeVariance: number,
  flipbook: ParticleEmitterFlipbookState | null
): number {
  if (baseLifetime === 0) {
    return IMMORTAL_PARTICLE_MAX_LIFE;
  }

  const rawLifetime = baseLifetime + (Math.random() - 0.5) * 2 * lifetimeVariance;
  return Math.max(0.001, capLifetimeIfNonLooping(rawLifetime, flipbook));
}

// Resolve bounding box lifetime
export function resolveBoundsLifetime(
  baseLifetime: number,
  lifetimeVariance: number,
  flipbook: ParticleEmitterFlipbookState | null
): number {
  if (baseLifetime === 0) {
    return IMMORTAL_PARTICLE_BOUNDS_MAX_LIFE;
  }

  const maxLifetime = baseLifetime + lifetimeVariance;
  return capLifetimeIfNonLooping(maxLifetime, flipbook);
}

// Attribute name constants
const ATTR_INITIAL_POSITION = 'initialPosition';
const ATTR_INITIAL_VELOCITY = 'initialVelocity';
const ATTR_SIZE_VAR = 'sizeVar'; // vec2: x=sizeStart, y=sizeEnd
const ATTR_TIME_VAR = 'timeVar'; // vec2: x=startTime, y=maxLife
const ATTR_OPACITY_VAR = 'opacityVar'; // vec2: x=opacityStart, y=opacityEnd
const ATTR_COLOR_START_VAR = 'colorStartVar';
const ATTR_COLOR_END_VAR = 'colorEndVar';

const UNIFORM_MAP = 'map';
const UNIFORM_TIME = 'time';
const UNIFORM_GRAVITY = 'gravity';
const UNIFORM_ALPHATEST = 'alphaTest';
const UNIFORM_ORIENTATION_MATRIX = 'orientationMatrix';
const UNIFORM_FLIPBOOK_COLUMNS = 'flipbookColumns';
const UNIFORM_FLIPBOOK_ROWS = 'flipbookRows';
const UNIFORM_FLIPBOOK_FRAME_COUNT = 'flipbookFrameCount';
const UNIFORM_FLIPBOOK_FPS = 'flipbookFps';
const UNIFORM_FLIPBOOK_LOOP = 'flipbookLoop';

const DEFINE_USE_ALPHATEST = 'USE_ALPHATEST';
const DEFINE_ORIENTATION_BILLBOARD = 'ORIENTATION_BILLBOARD';
const DEFINE_ORIENTATION_BILLBOARD_Y = 'ORIENTATION_BILLBOARD_Y';
const DEFINE_ORIENTATION_FIXED = 'ORIENTATION_FIXED';
const DEFINE_ORIENTATION_VELOCITY = 'ORIENTATION_VELOCITY';
const DEFINE_LOCK_TO_EMITTER = 'LOCK_TO_EMITTER';
const DEFINE_USE_FLIPBOOK = 'USE_FLIPBOOK';
const MAX_ATTACHMENT_CLEANUP_SLOTS_PER_FRAME = 2;
const MAX_PENDING_EMISSION_GROUPS = 256;

// Working variables
const attributes: InstancedBufferAttribute[] = [];
const tempVector3 = new Vector3();
const tempEuler = new Euler();
const tempMatrix3 = new Matrix3();
const tempMatrix4 = new Matrix4();
const DEG2RAD = Math.PI / 180;

// Helper function to convert orientation to define name
function orientationToDefine(orientation: ParticleEmitterOrientation): string {
  switch (orientation) {
    case 'billboard': return DEFINE_ORIENTATION_BILLBOARD;
    case 'billboardY': return DEFINE_ORIENTATION_BILLBOARD_Y;
    case 'fixed': return DEFINE_ORIENTATION_FIXED;
    case 'velocity': return DEFINE_ORIENTATION_VELOCITY;
    default: return DEFINE_ORIENTATION_BILLBOARD;
  }
}

// Helper function to create rotation matrix from Euler angles in degrees
// Note: Returns tempMatrix3, caller must copy if needed
function createOrientationMatrix(rotationDegrees: Vector3): Matrix3 {
  tempEuler.set(
    rotationDegrees.x * DEG2RAD,
    rotationDegrees.y * DEG2RAD,
    rotationDegrees.z * DEG2RAD,
    'XYZ'
  );
  tempMatrix3.setFromMatrix4(tempMatrix4.makeRotationFromEuler(tempEuler));
  return tempMatrix3;
}

export interface ParticleEmitterCoreOptions {
  alphaTest?: number;

  colorStart?: Color;
  colorEnd?: Color;
  colorStartVariance?: Color;
  colorEndVariance?: Color;
  colorIntensityStart?: number;
  colorIntensityEnd?: number;
  colorIntensityStartVariance?: number;
  colorIntensityEndVariance?: number;

  flipbook?: ParticleEmitterFlipbookOptions;

  gravity?: Vector3;

  lifetime?: number;
  lifetimeVariance?: number;

  // When enabled, emitted particles follow the emitter's world position.
  // IMPORTANT: This option cannot be changed after construction because
  // initialPosition attribute stores different coordinate spaces based on this flag:
  // - lockToEmitter=false: initialPosition is in world space
  // - lockToEmitter=true: initialPosition is in local space (relative to emitter)
  // Changing this at runtime would cause existing particles to render incorrectly.
  lockToEmitter?: boolean;

  maxParticles?: number;

  opacityEnd?: number;
  opacityEndVariance?: number;
  opacityStart?: number;
  opacityStartVariance?: number;

  orientation?: ParticleEmitterOrientation;
  orientationFixedRotation?: Vector3;

  position?: Vector3;
  positionVariance?: Vector3;

  rate?: number;
  rateVariance?: number;

  sizeEnd?: number;
  sizeEndVariance?: number;
  sizeStart?: number;
  sizeStartVariance?: number;

  texture?: Texture | null;

  transparent?: boolean;

  velocity?: Vector3;
  velocityVariance?: Vector3;
}

export type ParticleEmitterEmissionOverrides = Pick<ParticleEmitterCoreOptions,
  | 'colorEnd'
  | 'colorEndVariance'
  | 'colorIntensityEnd'
  | 'colorIntensityEndVariance'
  | 'colorIntensityStart'
  | 'colorIntensityStartVariance'
  | 'colorStart'
  | 'colorStartVariance'
  | 'lifetime'
  | 'lifetimeVariance'
  | 'opacityEnd'
  | 'opacityEndVariance'
  | 'opacityStart'
  | 'opacityStartVariance'
  | 'positionVariance'
  | 'sizeEnd'
  | 'sizeEndVariance'
  | 'sizeStart'
  | 'sizeStartVariance'
  | 'velocity'
  | 'velocityVariance'
>;

interface PendingEmission {
  count: number;
  attachmentSlot: number;
  emissionOverrides?: ParticleEmitterEmissionOverrides;
}

class ParticlesMaterial extends ShaderMaterial {
  constructor(lockToEmitter: boolean = false) {
    super({
      uniforms: {
        [UNIFORM_MAP]: { value: null },
        [UNIFORM_TIME]: { value: 0.0 },
        [UNIFORM_GRAVITY]: { value: new Vector3() },
        [UNIFORM_ORIENTATION_MATRIX]: { value: new Matrix3() },
        [UNIFORM_FLIPBOOK_COLUMNS]: { value: 1 },
        [UNIFORM_FLIPBOOK_ROWS]: { value: 1 },
        [UNIFORM_FLIPBOOK_FRAME_COUNT]: { value: 1 },
        [UNIFORM_FLIPBOOK_FPS]: { value: 30.0 },
        [UNIFORM_FLIPBOOK_LOOP]: { value: 1.0 },
      },
      defines: lockToEmitter ? {
        [DEFINE_LOCK_TO_EMITTER]: '',
        [DEFINE_ORIENTATION_BILLBOARD]: true
      } : { [DEFINE_ORIENTATION_BILLBOARD]: true },
      vertexShader: `
        uniform float ${UNIFORM_TIME};
        uniform vec3 ${UNIFORM_GRAVITY};
        uniform mat3 ${UNIFORM_ORIENTATION_MATRIX};

        #ifdef ${DEFINE_USE_FLIPBOOK}
          uniform float ${UNIFORM_FLIPBOOK_COLUMNS};
          uniform float ${UNIFORM_FLIPBOOK_ROWS};
          uniform float ${UNIFORM_FLIPBOOK_FRAME_COUNT};
          uniform float ${UNIFORM_FLIPBOOK_FPS};
          uniform float ${UNIFORM_FLIPBOOK_LOOP};
        #endif

        attribute vec3 ${ATTR_INITIAL_POSITION};
        attribute vec3 ${ATTR_INITIAL_VELOCITY};
        attribute vec2 ${ATTR_SIZE_VAR};
        attribute vec2 ${ATTR_TIME_VAR};
        attribute vec2 ${ATTR_OPACITY_VAR};
        attribute vec3 ${ATTR_COLOR_START_VAR};
        attribute vec3 ${ATTR_COLOR_END_VAR};

        varying vec2 vUv;
        varying float vLife;
        varying vec2 vOpacityVar;
        varying vec3 vColorStart;
        varying vec3 vColorEnd;

        void main() {
          float age = ${UNIFORM_TIME} - ${ATTR_TIME_VAR}.x;
          vLife = clamp(1.0 - age / ${ATTR_TIME_VAR}.y, 0.0, 1.0);

          // Pass variations to fragment shader
          vOpacityVar = ${ATTR_OPACITY_VAR};
          vColorStart = ${ATTR_COLOR_START_VAR};
          vColorEnd = ${ATTR_COLOR_END_VAR};

          if (vLife <= 0.0) {
            gl_Position = vec4(9999.0, 9999.0, 9999.0, 1.0);
            return;
          }

          // Calculate flipbook UV
          #ifdef ${DEFINE_USE_FLIPBOOK}
            float rawFrame = floor(age * ${UNIFORM_FLIPBOOK_FPS});
            float frameIndex;
            if (${UNIFORM_FLIPBOOK_LOOP} > 0.5) {
              frameIndex = mod(rawFrame, ${UNIFORM_FLIPBOOK_FRAME_COUNT});
            } else {
              frameIndex = min(rawFrame, ${UNIFORM_FLIPBOOK_FRAME_COUNT} - 1.0);
            }
            float col = mod(frameIndex, ${UNIFORM_FLIPBOOK_COLUMNS});
            float row = floor(frameIndex / ${UNIFORM_FLIPBOOK_COLUMNS});
            vec2 frameSize = vec2(1.0 / ${UNIFORM_FLIPBOOK_COLUMNS}, 1.0 / ${UNIFORM_FLIPBOOK_ROWS});
            vec2 frameOffset = vec2(col, row) * frameSize;
            vUv = frameOffset + uv * frameSize;
          #else
            vUv = uv;
          #endif

          // Physics calculation (same for both modes)
          vec3 physics = ${ATTR_INITIAL_POSITION} + ${ATTR_INITIAL_VELOCITY} * age + 0.5 * ${UNIFORM_GRAVITY} * age * age;

          // Calculate world position based on lock mode
          // - LOCK_TO_EMITTER: initialPosition is local, add current emitter world position
          // - Otherwise: initialPosition is already world position
          #ifdef ${DEFINE_LOCK_TO_EMITTER}
            vec3 worldPos = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz + physics;
          #else
            vec3 worldPos = physics;
          #endif

          // Calculate right and up vectors based on orientation mode
          vec3 particleRight;
          vec3 particleUp;

          #ifdef ${DEFINE_ORIENTATION_BILLBOARD}
            // Billboard: particles always face camera (use camera's right/up directly)
            particleRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
            particleUp = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
          #elif defined(${DEFINE_ORIENTATION_BILLBOARD_Y})
            // BillboardY: rotate around Y-axis to face camera view direction
            vec3 cameraForward = -vec3(viewMatrix[0][2], viewMatrix[1][2], viewMatrix[2][2]);
            vec3 forwardXZ = vec3(cameraForward.x, 0.0, cameraForward.z);
            if (length(forwardXZ) < 1e-4) {
              // Fallback when camera is looking nearly straight up or down
              forwardXZ = vec3(0.0, 0.0, 1.0);
            }
            particleRight = normalize(cross(forwardXZ, vec3(0.0, 1.0, 0.0)));
            particleUp = vec3(0.0, 1.0, 0.0);
          #elif defined(${DEFINE_ORIENTATION_FIXED})
            // Fixed: use orientation matrix
            particleRight = ${UNIFORM_ORIENTATION_MATRIX}[0];
            particleUp = ${UNIFORM_ORIENTATION_MATRIX}[1];
          #elif defined(${DEFINE_ORIENTATION_VELOCITY})
            // Velocity-aligned: particle Y-axis aligns to velocity direction,
            // billboards around that axis to face the camera.
            // v(t) = v0 + g * t
            vec3 currentVelocity = ${ATTR_INITIAL_VELOCITY} + ${UNIFORM_GRAVITY} * age;
            float velLength = length(currentVelocity);
            // When velocity is near zero, orientation is undefined. Skip rendering.
            if (velLength < 1e-7) {
              gl_Position = vec4(9999.0, 9999.0, 9999.0, 1.0);
              return;
            }
            particleUp = currentVelocity / velLength;
            vec3 cameraForward = -vec3(viewMatrix[0][2], viewMatrix[1][2], viewMatrix[2][2]);
            vec3 right = cross(cameraForward, particleUp);
            float rightLen = length(right);
            if (rightLen < 1e-4) {
              // Fallback when camera forward is nearly parallel to velocity direction.
              // Choose an arbitrary vector not parallel to particleUp to construct a basis.
              vec3 arbitrary = abs(particleUp.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
              right = cross(arbitrary, particleUp);
            }
            particleRight = normalize(right);
          #endif

          // Interpolate size based on life
          float interpolatedSize = mix(${ATTR_SIZE_VAR}.y, ${ATTR_SIZE_VAR}.x, vLife);

          // Use the vertex position from the plane geometry to create quad
          vec3 quadOffset = (position.x * particleRight + position.y * particleUp) * interpolatedSize;
          vec3 finalPos = worldPos + quadOffset;

          gl_Position = projectionMatrix * viewMatrix * vec4(finalPos, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D ${UNIFORM_MAP};

        #ifdef ${DEFINE_USE_ALPHATEST}
          uniform float ${UNIFORM_ALPHATEST};
        #endif

        varying vec2 vUv;
        varying float vLife;
        varying vec2 vOpacityVar;
        varying vec3 vColorStart;
        varying vec3 vColorEnd;

        void main() {
          vec4 texColor = texture2D(${UNIFORM_MAP}, vUv);

          vec3 color = mix(vColorEnd, vColorStart, vLife);
          float alphaGradient = mix(vOpacityVar.y, vOpacityVar.x, vLife);
          float finalAlpha = texColor.a * alphaGradient;

          #ifdef ${DEFINE_USE_ALPHATEST}
            if (finalAlpha < ${UNIFORM_ALPHATEST}) discard;
          #endif

          gl_FragColor = vec4(texColor.rgb * color, finalAlpha);
        }
      `,
      side: FrontSide,
    });
  }

  public setAlphaTest(alphaTest: number): this {
    if (alphaTest > 0) {
      if (!(UNIFORM_ALPHATEST in this.uniforms)) {
        this.uniforms[UNIFORM_ALPHATEST] = { value: alphaTest };
        this.defines[DEFINE_USE_ALPHATEST] = '';
        this.needsUpdate = true;
      } else {
        this.uniforms[UNIFORM_ALPHATEST].value = alphaTest;
      }
    } else if (alphaTest === 0 && (UNIFORM_ALPHATEST in this.uniforms)) {
      delete this.uniforms[UNIFORM_ALPHATEST];
      delete this.defines[DEFINE_USE_ALPHATEST];
      this.needsUpdate = true;
    }
    return this;
  }

  public setGravity(gravity: Vector3): this {
    this.uniforms[UNIFORM_GRAVITY].value.copy(gravity);
    return this;
  }

  public setOrientation(orientation: ParticleEmitterOrientation): this {
    // Clear all orientation defines
    delete this.defines[DEFINE_ORIENTATION_BILLBOARD];
    delete this.defines[DEFINE_ORIENTATION_BILLBOARD_Y];
    delete this.defines[DEFINE_ORIENTATION_FIXED];
    delete this.defines[DEFINE_ORIENTATION_VELOCITY];
    // Set the new one
    this.defines[orientationToDefine(orientation)] = true;
    // Set side based on orientation mode
    this.side = orientation === 'fixed' ? DoubleSide : FrontSide;
    this.needsUpdate = true;
    return this;
  }

  public setOrientationFixedRotation(rotation: Vector3): this {
    this.uniforms[UNIFORM_ORIENTATION_MATRIX].value.copy(createOrientationMatrix(rotation));
    return this;
  }

  public setTexture(texture: Texture | null): this {
    this.uniforms[UNIFORM_MAP].value = texture;
    this.visible = !!texture;
    return this;
  }

  public setTransparent(transparent: boolean): this {
    if (this.transparent !== transparent) {
      this.needsUpdate = true;
    }
    this.transparent = transparent;
    this.depthWrite = !this.transparent;
    return this;
  }

  public updateTime(time: number): void {
    this.uniforms.time.value = time;
  }

  public setFlipbook(columns: number, rows: number, fps: number, loop: boolean): this {
    const isEnabled = columns > 1 || rows > 1;

    if (isEnabled) {
      if (!(DEFINE_USE_FLIPBOOK in this.defines)) {
        this.defines[DEFINE_USE_FLIPBOOK] = '';
        this.needsUpdate = true;
      }
      this.uniforms[UNIFORM_FLIPBOOK_COLUMNS].value = columns;
      this.uniforms[UNIFORM_FLIPBOOK_ROWS].value = rows;
      this.uniforms[UNIFORM_FLIPBOOK_FRAME_COUNT].value = columns * rows;
      this.uniforms[UNIFORM_FLIPBOOK_FPS].value = fps;
      this.uniforms[UNIFORM_FLIPBOOK_LOOP].value = loop ? 1.0 : 0.0;
    } else if (DEFINE_USE_FLIPBOOK in this.defines) {
      delete this.defines[DEFINE_USE_FLIPBOOK];
      this.needsUpdate = true;
    }

    return this;
  }
}

const defaults: Required<Omit<ParticleEmitterCoreOptions, 'flipbook'>> & { flipbook: ParticleEmitterFlipbookOptions | undefined } = {
  alphaTest: 0,
  colorEnd: new Color(1, 1, 1),
  colorEndVariance: new Color(0, 0, 0),
  colorStart: new Color(1, 1, 1),
  colorStartVariance: new Color(0, 0, 0),
  colorIntensityStart: 1.0,
  colorIntensityEnd: 1.0,
  colorIntensityStartVariance: 0,
  colorIntensityEndVariance: 0,
  flipbook: undefined,
  lifetime: 1.0,
  lifetimeVariance: 0,
  lockToEmitter: false,
  maxParticles: 0, // Will be calculated
  gravity: new Vector3(0, 0, 0),
  opacityEnd: 0,
  opacityEndVariance: 0,
  opacityStart: 1,
  opacityStartVariance: 0,
  orientation: 'billboard',
  orientationFixedRotation: new Vector3(0, 0, 0),
  position: new Vector3(0, 0, 0),
  positionVariance: new Vector3(0, 0, 0),
  rate: 10,
  rateVariance: 0,
  sizeEnd: 1.0,
  sizeEndVariance: 0,
  sizeStart: 1.0,
  sizeStartVariance: 0,
  texture: null,
  transparent: false,
  velocity: new Vector3(0, 0, 0),
  velocityVariance: new Vector3(0, 0, 0),
};

// Note:
// - For each emitter core, one InstancedMesh and one Material are created.
//   A large number of particles from a single emitter core are rendered
//   in one draw call.
// - For simplicity, particle appearance is always defined by a texture.
// - InstancedMesh is created synchronously for simplicity.
// - When an emitter core is created, its texture is set to null by default,
//   and can be assigned later. While the texture is null, the emitter
//   remains invisible.
// - Particles are emitted relative to the emitter's world position.
//   After emission, particles do not follow emitter movement.
//
// TODO:
// - If rendering performance becomes an issue when many emitters are
//   created, consider rendering multiple emitters using a single
//   InstancedMesh in one draw call.
export default class ParticleEmitterCore {
  private _options: Required<Omit<ParticleEmitterCoreOptions, 'flipbook'>> & { flipbook: ParticleEmitterFlipbookOptions | undefined };
  private _poolIndex: number;
  private _currentTime: number;
  private _emissionAccumulator: number;
  private _isMaxParticlesAutoCalculated: boolean = false;
  private _mesh: InstancedMesh;
  private _paused: boolean = false;
  private _flipbook: ParticleEmitterFlipbookState | null = null;
  private _pendingEmissions: Map<string, PendingEmission> = new Map();
  private _pendingEmissionOverrideIds: WeakMap<object, number> = new WeakMap();
  private _nextPendingEmissionOverrideId: number = 1;

  // Attachment slot system for per-instance matrices (lockToEmitter mode)
  private _particleAttachmentSlots: Uint32Array | null = null;
  private _attachmentMatrices: Map<number, Matrix4> = new Map();
  private _particleIndicesByAttachmentSlot: Map<number, Set<number>> = new Map();
  private _dirtyAttachmentSlots: Set<number> = new Set();
  private _cleanupAttachmentSlots: Set<number> = new Set();

  constructor(options: ParticleEmitterCoreOptions) {
    this._options = { ...defaults };

    // Only override defaults with defined values, otherwise if we use spread, options overrides all defaults since it has keys.
    for (const key in options) {
      if (options[key as keyof ParticleEmitterCoreOptions] !== undefined) {
        (this._options as any)[key] = options[key as keyof ParticleEmitterCoreOptions];
      }
    }

    // Auto-calculate maxParticles if needed
    if (!this._options.maxParticles) {
      this._options.maxParticles = this._calculateMaxParticles();
      this._isMaxParticlesAutoCalculated = true;
    }

    // Clone Vector3 and Color instances to avoid shared references
    for (const key in this._options) {
      const value = this._options[key as keyof typeof this._options];
      if (value instanceof Vector3 || value instanceof Color) {
        // TODO: Avoid any if possible
        (this._options as any)[key] = value.clone();
      }
    }

    // Initialize flipbook state if provided
    if (isValidFlipbook(this._options.flipbook)) {
      this._flipbook = toFlipbookState(this._options.flipbook);
    }

    const geometry = this._createGeometry(this._options.maxParticles);

    const material = new ParticlesMaterial(this._options.lockToEmitter)
      .setTexture(this._options.texture)
      .setGravity(this._options.gravity)
      .setTransparent(this._options.transparent)
      .setAlphaTest(this._options.alphaTest)
      .setOrientation(this._options.orientation)
      .setOrientationFixedRotation(this._options.orientationFixedRotation);

    // Set flipbook on material
    if (this._flipbook) {
      material.setFlipbook(this._flipbook.columns, this._flipbook.rows, this._flipbook.fps, this._flipbook.loop);
    }

    this._mesh = new InstancedMesh(
      geometry,
      material,
      this._options.maxParticles,
    );
    this._mesh.position.copy(this._options.position);

    // Enable instanceMatrix for lockToEmitter mode
    if (this._options.lockToEmitter) {
      this._mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      this._particleAttachmentSlots = new Uint32Array(this._options.maxParticles);
      // Initialize all instance matrices to identity
      const identity = new Matrix4();
      for (let i = 0; i < this._options.maxParticles; i++) {
        this._mesh.setMatrixAt(i, identity);
      }
      this._mesh.instanceMatrix.needsUpdate = true;
    }

    this._updateBoundingBox();

    this._poolIndex = 0;
    this._currentTime = 0;
    this._emissionAccumulator = 0;
  }

  public get mesh(): InstancedMesh {
    return this._mesh;
  }

  public get paused(): boolean {
    return this._paused;
  }

  public get flipbook(): ParticleEmitterFlipbookState | null {
    return this._flipbook;
  }

  public burst(
    count: number,
    attachmentSlot: number = 0,
    emissionOverrides?: ParticleEmitterEmissionOverrides,
  ): void {
    // Burst emits particles immediately regardless of pause state

    if (count <= 0) {
      return;
    }

    const material = this.mesh.material as ParticlesMaterial;
    if (!material.visible) {
      this._enqueuePendingEmission(count, attachmentSlot, emissionOverrides);
      return;
    }

    if (this.mesh.matrixWorldAutoUpdate) {
      this.mesh.updateMatrixWorld();
    }

    this._flushPendingEmissions();
    this._emit(count, this.mesh.matrixWorld, attachmentSlot, emissionOverrides);
  }

  public pause(): void {
    this._paused = true;
    // Reset accumulator when pausing to discard fractional particle accumulation
    this._emissionAccumulator = 0;
  }

  public restart(): void {
    this._paused = false;
  }

  private _calculateMaxParticles(): number {
    const maxLife = this._options.lifetime + this._options.lifetimeVariance;
    const theoreticalMax = Math.ceil(this._options.rate * maxLife);
    const safetyMargin = 1.2;  // 20% safety margin
    const calculatedMax = Math.ceil(theoreticalMax * safetyMargin);
    const minimumParticles = 10;  // Minimum particle count
    return Math.max(minimumParticles, calculatedMax);
  }

  private _calculateBoundingBox(): Box3 {
    const maxLife = resolveBoundsLifetime(this._options.lifetime, this._options.lifetimeVariance, this._flipbook);
    // Calculate maximum possible size considering both start and end sizes
    const maxSizeStart = this._options.sizeStart + this._options.sizeStartVariance;
    const maxSizeEnd = this._options.sizeEnd + this._options.sizeEndVariance;
    const maxSize = Math.max(maxSizeStart, maxSizeEnd);

    // Maximum initial position range
    const posVar = this._options.positionVariance;

    // Maximum velocity (per axis)
    const maxVelX = Math.abs(this._options.velocity.x) + this._options.velocityVariance.x;
    const maxVelY = Math.abs(this._options.velocity.y) + this._options.velocityVariance.y;
    const maxVelZ = Math.abs(this._options.velocity.z) + this._options.velocityVariance.z;

    // Maximum displacement due to gravity
    const gravityDisplacement = new Vector3(
      0.5 * Math.abs(this._options.gravity.x) * maxLife * maxLife,
      0.5 * Math.abs(this._options.gravity.y) * maxLife * maxLife,
      0.5 * Math.abs(this._options.gravity.z) * maxLife * maxLife,
    );

    // Maximum displacement due to velocity
    const velocityDisplacement = new Vector3(
      maxVelX * maxLife,
      maxVelY * maxLife,
      maxVelZ * maxLife,
    );

    // Calculate overall maximum range
    const maxRange = new Vector3(
      posVar.x + velocityDisplacement.x + gravityDisplacement.x + maxSize,
      posVar.y + velocityDisplacement.y + gravityDisplacement.y + maxSize,
      posVar.z + velocityDisplacement.z + gravityDisplacement.z + maxSize,
    );

    // Create bounding box
    const min = new Vector3(-maxRange.x, -maxRange.y, -maxRange.z);
    const max = new Vector3(maxRange.x, maxRange.y, maxRange.z);

    // Adjust based on velocity direction
    ((this._options.velocity.x > 0) ? max : min).x += this._options.velocity.x * maxLife;
    ((this._options.velocity.y > 0) ? max : min).y += this._options.velocity.y * maxLife;
    ((this._options.velocity.z > 0) ? max : min).z += this._options.velocity.z * maxLife;

    return new Box3(min, max);
  }

  private _updateBoundingBox(): void {
    const boundingBox = this._calculateBoundingBox();
    this.mesh.geometry.boundingBox = boundingBox;

    // Also calculate bounding sphere
    const center = new Vector3();
    boundingBox.getCenter(center);
    const radius = boundingBox.getSize(new Vector3()).length() * 0.5;
    this.mesh.geometry.boundingSphere = new Sphere(center, radius);
  }

  private _emit(
    count: number,
    worldMatrix: Matrix4,
    attachmentSlot: number = 0,
    emissionOverrides?: ParticleEmitterEmissionOverrides,
  ): void {
    const geometry = this.mesh.geometry;
    const initialPositionAttr = geometry.getAttribute(ATTR_INITIAL_POSITION) as InstancedBufferAttribute;
    const initialVelocityAttr = geometry.getAttribute(ATTR_INITIAL_VELOCITY) as InstancedBufferAttribute;
    const sizeVarAttr = geometry.getAttribute(ATTR_SIZE_VAR) as InstancedBufferAttribute;
    const timeVarAttr = geometry.getAttribute(ATTR_TIME_VAR) as InstancedBufferAttribute;
    const opacityVarAttr = geometry.getAttribute(ATTR_OPACITY_VAR) as InstancedBufferAttribute;
    const colorStartVarAttr = geometry.getAttribute(ATTR_COLOR_START_VAR) as InstancedBufferAttribute;
    const colorEndVarAttr = geometry.getAttribute(ATTR_COLOR_END_VAR) as InstancedBufferAttribute;

    // Track the range of indices that need updating
    const startIndex = this._poolIndex;
    let actualEmitCount = 0;
    let instanceMatrixNeedsUpdate = false;

    const colorEnd = emissionOverrides?.colorEnd ?? this._options.colorEnd;
    const colorEndVariance = emissionOverrides?.colorEndVariance ?? this._options.colorEndVariance;
    const colorStart = emissionOverrides?.colorStart ?? this._options.colorStart;
    const colorStartVariance = emissionOverrides?.colorStartVariance ?? this._options.colorStartVariance;
    const colorIntensityEnd = emissionOverrides?.colorIntensityEnd ?? this._options.colorIntensityEnd;
    const colorIntensityEndVariance = emissionOverrides?.colorIntensityEndVariance ?? this._options.colorIntensityEndVariance;
    const colorIntensityStart = emissionOverrides?.colorIntensityStart ?? this._options.colorIntensityStart;
    const colorIntensityStartVariance = emissionOverrides?.colorIntensityStartVariance ?? this._options.colorIntensityStartVariance;
    const lifetime = emissionOverrides?.lifetime ?? this._options.lifetime;
    const lifetimeVariance = emissionOverrides?.lifetimeVariance ?? this._options.lifetimeVariance;
    const opacityEnd = emissionOverrides?.opacityEnd ?? this._options.opacityEnd;
    const opacityEndVariance = emissionOverrides?.opacityEndVariance ?? this._options.opacityEndVariance;
    const opacityStart = emissionOverrides?.opacityStart ?? this._options.opacityStart;
    const opacityStartVariance = emissionOverrides?.opacityStartVariance ?? this._options.opacityStartVariance;
    const positionVariance = emissionOverrides?.positionVariance ?? this._options.positionVariance;
    const sizeEnd = emissionOverrides?.sizeEnd ?? this._options.sizeEnd;
    const sizeEndVariance = emissionOverrides?.sizeEndVariance ?? this._options.sizeEndVariance;
    const sizeStart = emissionOverrides?.sizeStart ?? this._options.sizeStart;
    const sizeStartVariance = emissionOverrides?.sizeStartVariance ?? this._options.sizeStartVariance;
    const velocity = emissionOverrides?.velocity ?? this._options.velocity;
    const velocityVariance = emissionOverrides?.velocityVariance ?? this._options.velocityVariance;

    for (let i = 0; i < count; i++) {
      // Generate local position variance
      const localPosX = (Math.random() - 0.5) * 2 * positionVariance.x;
      const localPosY = (Math.random() - 0.5) * 2 * positionVariance.y;
      const localPosZ = (Math.random() - 0.5) * 2 * positionVariance.z;

      tempVector3.set(localPosX, localPosY, localPosZ);

      // When lockToEmitter is enabled, store local position.
      // Otherwise, transform to world space.
      if (!this._options.lockToEmitter) {
        tempVector3.applyMatrix4(worldMatrix);
      }

      const velX = velocity.x + (Math.random() - 0.5) * 2 * velocityVariance.x;
      const velY = velocity.y + (Math.random() - 0.5) * 2 * velocityVariance.y;
      const velZ = velocity.z + (Math.random() - 0.5) * 2 * velocityVariance.z;

      // Use flipbook-aware lifetime
      const maxLife = resolveRuntimeParticleLifetime(lifetime, lifetimeVariance, this._flipbook);

      const particleSizeStart = Math.max(0, sizeStart + (Math.random() - 0.5) * 2 * sizeStartVariance);
      const particleSizeEnd = Math.max(0, sizeEnd + (Math.random() - 0.5) * 2 * sizeEndVariance);

      const particleOpacityStart = Math.max(0, Math.min(1, opacityStart + (Math.random() - 0.5) * 2 * opacityStartVariance));
      const particleOpacityEnd = Math.max(0, Math.min(1, opacityEnd + (Math.random() - 0.5) * 2 * opacityEndVariance));

      const intensityStart = Math.max(0, colorIntensityStart + (Math.random() - 0.5) * 2 * colorIntensityStartVariance);
      const intensityEnd = Math.max(0, colorIntensityEnd + (Math.random() - 0.5) * 2 * colorIntensityEndVariance);

      const colorStartR = Math.max(0, colorStart.r + (Math.random() - 0.5) * 2 * colorStartVariance.r) * intensityStart;
      const colorStartG = Math.max(0, colorStart.g + (Math.random() - 0.5) * 2 * colorStartVariance.g) * intensityStart;
      const colorStartB = Math.max(0, colorStart.b + (Math.random() - 0.5) * 2 * colorStartVariance.b) * intensityStart;

      const colorEndR = Math.max(0, colorEnd.r + (Math.random() - 0.5) * 2 * colorEndVariance.r) * intensityEnd;
      const colorEndG = Math.max(0, colorEnd.g + (Math.random() - 0.5) * 2 * colorEndVariance.g) * intensityEnd;
      const colorEndB = Math.max(0, colorEnd.b + (Math.random() - 0.5) * 2 * colorEndVariance.b) * intensityEnd;

      initialPositionAttr.setXYZ(this._poolIndex, tempVector3.x, tempVector3.y, tempVector3.z);
      initialVelocityAttr.setXYZ(this._poolIndex, velX, velY, velZ);
      sizeVarAttr.setXY(this._poolIndex, particleSizeStart, particleSizeEnd);
      timeVarAttr.setXY(this._poolIndex, this._currentTime, maxLife);
      opacityVarAttr.setXY(this._poolIndex, particleOpacityStart, particleOpacityEnd);
      colorStartVarAttr.setXYZ(this._poolIndex, colorStartR, colorStartG, colorStartB);
      colorEndVarAttr.setXYZ(this._poolIndex, colorEndR, colorEndG, colorEndB);

      // Track attachment slot for this particle (lockToEmitter mode only)
      if (this._particleAttachmentSlots && this._options.lockToEmitter) {
        const previousSlot = this._particleAttachmentSlots[this._poolIndex];
        const previousIndices = this._particleIndicesByAttachmentSlot.get(previousSlot);
        if (previousIndices) {
          previousIndices.delete(this._poolIndex);
          if (previousIndices.size === 0) {
            this._particleIndicesByAttachmentSlot.delete(previousSlot);
            this._cleanupAttachmentSlots.delete(previousSlot);

            // Keep matrix when reusing the same slot in this emit iteration.
            if (previousSlot !== attachmentSlot) {
              this._dirtyAttachmentSlots.delete(previousSlot);
              this._attachmentMatrices.delete(previousSlot);
            }
          }
        }

        this._particleAttachmentSlots[this._poolIndex] = attachmentSlot;

        // Track particle indices by slot
        if (!this._particleIndicesByAttachmentSlot.has(attachmentSlot)) {
          this._particleIndicesByAttachmentSlot.set(attachmentSlot, new Set());
        }
        this._particleIndicesByAttachmentSlot.get(attachmentSlot)!.add(this._poolIndex);
        this._cleanupAttachmentSlots.add(attachmentSlot);

        const attachmentMatrix = this._attachmentMatrices.get(attachmentSlot) ?? worldMatrix;
        this._mesh.setMatrixAt(this._poolIndex, attachmentMatrix);
        instanceMatrixNeedsUpdate = true;
      }

      this._poolIndex = (this._poolIndex + 1) % this._options.maxParticles;
      actualEmitCount++;
    }

    attributes.push(initialPositionAttr);
    attributes.push(initialVelocityAttr);
    attributes.push(sizeVarAttr);
    attributes.push(timeVarAttr);
    attributes.push(opacityVarAttr);
    attributes.push(colorStartVarAttr);
    attributes.push(colorEndVarAttr);

    // Update only the range that was modified.
    // Do not clear update ranges here because _emit can be called multiple
    // times in the same frame (e.g. flushing multiple pending emission groups).
    // Clearing would drop ranges from earlier emits in the same frame.
    if (actualEmitCount > 0) {
      if (startIndex + actualEmitCount <= this._options.maxParticles) {
        // Simple case: no wrap-around
        attributes.forEach(attribute => attribute.addUpdateRange(startIndex * attribute.itemSize, actualEmitCount * attribute.itemSize));
      } else {
        // Wrap-around case: update in two parts
        const firstPartCount = this._options.maxParticles - startIndex;
        const secondPartCount = actualEmitCount - firstPartCount;

        attributes.forEach(attribute => {
          // Update first part (from startIndex to end)
          attribute.addUpdateRange(startIndex * attribute.itemSize, firstPartCount * attribute.itemSize);
          // Update second part (from 0 to wrap point)
          attribute.addUpdateRange(0, secondPartCount * attribute.itemSize);
        });
      }

      attributes.forEach(attribute => attribute.needsUpdate = true);
    }

    if (instanceMatrixNeedsUpdate) {
      this._mesh.instanceMatrix.needsUpdate = true;
    }

    attributes.length = 0;
  }

  private _getCurrentRate(): number {
    if (this._options.rateVariance === 0) {
      return this._options.rate;
    }

    // Apply random variation to rate
    const variance = (Math.random() - 0.5) * 2 * this._options.rateVariance;

    // Ensure rate doesn't go negative
    return Math.max(0, this._options.rate + variance);
  }

  public update(deltaTimeS: number): void {
    // Clamp deltaTime to prevent large jumps
    const clampedDeltaTime = Math.min(deltaTimeS, 0.1);
    const material = this.mesh.material as ParticlesMaterial;

    this._currentTime += deltaTimeS;  // Track actual time accurately
    material.updateTime(this._currentTime);

    if (!material.visible) {
      return;
    }

    if (this.mesh.matrixWorldAutoUpdate) {
      this.mesh.updateMatrixWorld();
    }

    this._flushPendingEmissions();

    if (this._paused) {
      return;
    }

    // Get current rate (with variance applied)
    const currentRate = this._getCurrentRate();

    // Accumulate using clamped deltaTime
    this._emissionAccumulator += currentRate * clampedDeltaTime;

    // Calculate and limit particles to emit
    const particlesToEmit = Math.floor(this._emissionAccumulator);

    if (particlesToEmit > 0) {
      if (this.mesh.matrixWorldAutoUpdate) {
        this.mesh.updateMatrixWorld();
      }
      this._emit(particlesToEmit, this.mesh.matrixWorld);
      this._emissionAccumulator -= particlesToEmit;
    }
  }

  public updateParameters(updates: Partial<ParticleEmitterCoreOptions>): void {
    const material = this.mesh.material as ParticlesMaterial;

    let boundingBoxNeedsUpdate = false;
    let needsAutomaticResize = false;

    if (updates.alphaTest !== undefined) {
      this._options.alphaTest = updates.alphaTest;
      material.setAlphaTest(this._options.alphaTest);
    }

    if (updates.colorEnd !== undefined) {
      this._options.colorEnd.copy(updates.colorEnd);
    }

    if (updates.colorEndVariance !== undefined) {
      this._options.colorEndVariance.copy(updates.colorEndVariance);
    }

    if (updates.colorStart !== undefined) {
      this._options.colorStart.copy(updates.colorStart);
    }

    if (updates.colorStartVariance !== undefined) {
      this._options.colorStartVariance.copy(updates.colorStartVariance);
    }

    if (updates.colorIntensityStart !== undefined) {
      this._options.colorIntensityStart = updates.colorIntensityStart;
    }

    if (updates.colorIntensityEnd !== undefined) {
      this._options.colorIntensityEnd = updates.colorIntensityEnd;
    }

    if (updates.colorIntensityStartVariance !== undefined) {
      this._options.colorIntensityStartVariance = updates.colorIntensityStartVariance;
    }

    if (updates.colorIntensityEndVariance !== undefined) {
      this._options.colorIntensityEndVariance = updates.colorIntensityEndVariance;
    }

    if (updates.flipbook !== undefined) {
      this._options.flipbook = updates.flipbook;
      if (isValidFlipbook(updates.flipbook)) {
        this._flipbook = toFlipbookState(updates.flipbook);
        material.setFlipbook(this._flipbook.columns, this._flipbook.rows, this._flipbook.fps, this._flipbook.loop);
      } else {
        this._flipbook = null;
        material.setFlipbook(1, 1, 30, true);
      }
      boundingBoxNeedsUpdate = true;
    }

    if (updates.gravity !== undefined) {
      this._options.gravity.copy(updates.gravity);
      material.setGravity(this._options.gravity);
    }

    if (updates.lifetime !== undefined && this._options.lifetime !== updates.lifetime) {
      this._options.lifetime = updates.lifetime;
      boundingBoxNeedsUpdate = true;
      needsAutomaticResize = true;
    }

    if (updates.lifetimeVariance !== undefined && this._options.lifetimeVariance !== updates.lifetimeVariance) {
      this._options.lifetimeVariance = updates.lifetimeVariance;
      boundingBoxNeedsUpdate = true;
      needsAutomaticResize = true;
    }

    if (updates.opacityEnd !== undefined) {
      this._options.opacityEnd = updates.opacityEnd;
    }

    if (updates.opacityEndVariance !== undefined) {
      this._options.opacityEndVariance = updates.opacityEndVariance;
    }

    if (updates.opacityStart !== undefined) {
      this._options.opacityStart = updates.opacityStart;
    }

    if (updates.opacityStartVariance !== undefined) {
      this._options.opacityStartVariance = updates.opacityStartVariance;
    }

    if (updates.position !== undefined) {
      this._options.position.copy(updates.position);
      this.mesh.position.copy(this._options.position);
    }

    if (updates.positionVariance !== undefined && !this._options.positionVariance.equals(updates.positionVariance)) {
      this._options.positionVariance.copy(updates.positionVariance);
      boundingBoxNeedsUpdate = true;
    }

    if (updates.rate !== undefined) {
      if (this._options.rate !== updates.rate) {
        needsAutomaticResize = true;
      }
      this._options.rate = updates.rate;
    }

    if (updates.rateVariance !== undefined) {
      this._options.rateVariance = updates.rateVariance;
    }

    if (updates.sizeEnd !== undefined && this._options.sizeEnd !== updates.sizeEnd) {
      this._options.sizeEnd = updates.sizeEnd;
      boundingBoxNeedsUpdate = true;
    }

    if (updates.sizeEndVariance !== undefined && this._options.sizeEndVariance !== updates.sizeEndVariance) {
      this._options.sizeEndVariance = updates.sizeEndVariance;
      boundingBoxNeedsUpdate = true;
    }

    if (updates.sizeStart !== undefined && this._options.sizeStart !== updates.sizeStart) {
      this._options.sizeStart = updates.sizeStart;
      boundingBoxNeedsUpdate = true;
    }

    if (updates.sizeStartVariance !== undefined && this._options.sizeStartVariance !== updates.sizeStartVariance) {
      this._options.sizeStartVariance = updates.sizeStartVariance;
      boundingBoxNeedsUpdate = true;
    }

    if (updates.orientation !== undefined) {
      this._options.orientation = updates.orientation;
      material.setOrientation(this._options.orientation);
    }

    if (updates.orientationFixedRotation !== undefined) {
      this._options.orientationFixedRotation = updates.orientationFixedRotation;
      material.setOrientationFixedRotation(this._options.orientationFixedRotation);
    }

    if (updates.texture !== undefined) {
      this._options.texture = updates.texture;
      material.setTexture(this._options.texture);
    }

    if (updates.transparent !== undefined) {
      this._options.transparent = updates.transparent;
      material.setTransparent(this._options.transparent);
    }

    if (updates.velocity !== undefined && !this._options.velocity.equals(updates.velocity)) {
      this._options.velocity.copy(updates.velocity);
      boundingBoxNeedsUpdate = true;
    }

    if (updates.velocityVariance !== undefined && !this._options.velocityVariance.equals(updates.velocityVariance)) {
      this._options.velocityVariance.copy(updates.velocityVariance);
      boundingBoxNeedsUpdate = true;
    }

    const oldMaxParticles = this._options.maxParticles;

    if (updates.maxParticles !== undefined && updates.maxParticles > 0) {
      this._options.maxParticles = updates.maxParticles;
      this._isMaxParticlesAutoCalculated = false;
    } else if (updates.maxParticles === 0 || (this._isMaxParticlesAutoCalculated && needsAutomaticResize)) {
      this._options.maxParticles = this._calculateMaxParticles();
      this._isMaxParticlesAutoCalculated = true;
    }

    if (this._options.maxParticles !== oldMaxParticles) {
      this._resize();
    }

    if (boundingBoxNeedsUpdate) {
      this._updateBoundingBox();
    }
  }

  private _resize(): void {
    const oldGeometry = this.mesh.geometry as PlaneGeometry;
    const oldInstanceMatrix = this.mesh.instanceMatrix;
    const newGeometry = this._createGeometry(this._options.maxParticles);

    // Copy existing particle data across the full old buffer range.
    // `_poolIndex` is a ring-buffer write cursor, not a live-particle count.
    const oldParticleCapacity = (oldGeometry.getAttribute(ATTR_TIME_VAR) as InstancedBufferAttribute).count;
    const copyCount = Math.min(oldParticleCapacity, this._options.maxParticles);
    if (copyCount > 0) {
      this._copyParticleData(oldGeometry, newGeometry, copyCount);
    }

    newGeometry.boundingBox = oldGeometry.boundingBox;
    newGeometry.boundingSphere = oldGeometry.boundingSphere;

    // Don't know if switching a geometry in InstancedMesh to a new one is a good design.
    this.mesh.geometry = newGeometry;
    // TODO: Maybe updating InstancedMesh.count would be hacky. Switch to more elegant way.
    this.mesh.count = this._options.maxParticles;

    // Keep instance matrix capacity in sync with mesh.count.
    // InstancedMesh does not automatically resize instanceMatrix when count changes.
    const newInstanceMatrix = new InstancedBufferAttribute(new Float32Array(this._options.maxParticles * 16), 16);
    newInstanceMatrix.setUsage(oldInstanceMatrix.usage);

    const oldInstanceArray = oldInstanceMatrix.array as Float32Array;
    const newInstanceArray = newInstanceMatrix.array as Float32Array;
    const copyLength = Math.min(oldInstanceArray.length, newInstanceArray.length);
    newInstanceArray.set(oldInstanceArray.subarray(0, copyLength), 0);

    const oldInstanceCount = oldInstanceMatrix.count;
    if (this._options.maxParticles > oldInstanceCount) {
      const identity = new Matrix4().elements;
      for (let i = oldInstanceCount; i < this._options.maxParticles; i++) {
        newInstanceArray.set(identity, i * 16);
      }
    }

    // Dispose current instanced-mesh GPU resources first so the old
    // instanceMatrix GL buffer is released before swapping attributes.
    this.mesh.dispose();
    this.mesh.instanceMatrix = newInstanceMatrix;
    this.mesh.instanceMatrix.needsUpdate = true;

    oldGeometry.dispose();

    // Adjust pool index if needed
    if (this._poolIndex >= this._options.maxParticles) {
      this._poolIndex = 0;
    }

    if (this._options.lockToEmitter) {
      const oldAttachmentSlots = this._particleAttachmentSlots;
      this._particleAttachmentSlots = new Uint32Array(this._options.maxParticles);

      if (oldAttachmentSlots) {
        const copyLength = Math.min(oldAttachmentSlots.length, this._particleAttachmentSlots.length);
        this._particleAttachmentSlots.set(oldAttachmentSlots.subarray(0, copyLength), 0);
      }

      for (const [slot, indices] of this._particleIndicesByAttachmentSlot.entries()) {
        for (const idx of indices) {
          if (idx >= this._options.maxParticles) {
            indices.delete(idx);
          }
        }

        if (indices.size === 0) {
          this._particleIndicesByAttachmentSlot.delete(slot);
          this._dirtyAttachmentSlots.delete(slot);
          this._cleanupAttachmentSlots.delete(slot);
          this._attachmentMatrices.delete(slot);
        }
      }
    }
  }

  private _createGeometry(maxParticles: number): PlaneGeometry {
    return new PlaneGeometry(1, 1)
      .setAttribute(ATTR_INITIAL_POSITION, new InstancedBufferAttribute(new Float32Array(maxParticles * 3), 3).setUsage(DynamicDrawUsage))
      .setAttribute(ATTR_INITIAL_VELOCITY, new InstancedBufferAttribute(new Float32Array(maxParticles * 3), 3).setUsage(DynamicDrawUsage))
      .setAttribute(ATTR_SIZE_VAR, new InstancedBufferAttribute(new Float32Array(maxParticles * 2), 2).setUsage(DynamicDrawUsage))
      .setAttribute(ATTR_TIME_VAR, new InstancedBufferAttribute(new Float32Array(maxParticles * 2), 2).setUsage(DynamicDrawUsage))
      .setAttribute(ATTR_OPACITY_VAR, new InstancedBufferAttribute(new Float32Array(maxParticles * 2), 2).setUsage(DynamicDrawUsage))
      .setAttribute(ATTR_COLOR_START_VAR, new InstancedBufferAttribute(new Float32Array(maxParticles * 3), 3).setUsage(DynamicDrawUsage))
      .setAttribute(ATTR_COLOR_END_VAR, new InstancedBufferAttribute(new Float32Array(maxParticles * 3), 3).setUsage(DynamicDrawUsage))
      .deleteAttribute('normal');
  }

  private _copyParticleData(oldGeometry: PlaneGeometry, newGeometry: PlaneGeometry, count: number): void {
    this._copyAttribute(oldGeometry, newGeometry, ATTR_INITIAL_POSITION, count);
    this._copyAttribute(oldGeometry, newGeometry, ATTR_INITIAL_VELOCITY, count);
    this._copyAttribute(oldGeometry, newGeometry, ATTR_SIZE_VAR, count);
    this._copyAttribute(oldGeometry, newGeometry, ATTR_TIME_VAR, count);
    this._copyAttribute(oldGeometry, newGeometry, ATTR_OPACITY_VAR, count);
    this._copyAttribute(oldGeometry, newGeometry, ATTR_COLOR_START_VAR, count);
    this._copyAttribute(oldGeometry, newGeometry, ATTR_COLOR_END_VAR, count);
  }

  private _copyAttribute(oldGeometry: PlaneGeometry, newGeometry: PlaneGeometry, attrName: string, count: number): void {
    const oldAttr = oldGeometry.getAttribute(attrName) as InstancedBufferAttribute;
    const newAttr = newGeometry.getAttribute(attrName) as InstancedBufferAttribute;

    const copySize = count * oldAttr.itemSize;
    const oldArray = oldAttr.array as Float32Array;
    const newArray = newAttr.array as Float32Array;

    for (let i = 0; i < copySize; i++) {
      newArray[i] = oldArray[i];
    }

    newAttr.needsUpdate = true;
  }

  // Attachment slot management methods for lockToEmitter mode

  /**
   * Kill all particles associated with the given attachment slot.
   * This sets their lifetime to 0 so they are culled in the shader.
   */
  public killAttachmentSlot(slot: number): void {
    if (!this._particleAttachmentSlots || !this._options.lockToEmitter) {
      return;
    }

    const indices = this._particleIndicesByAttachmentSlot.get(slot);
    if (!indices || indices.size === 0) {
      return;
    }

    const geometry = this.mesh.geometry;
    const timeVarAttr = geometry.getAttribute(ATTR_TIME_VAR) as InstancedBufferAttribute;

    for (const idx of indices) {
      // Set maxLife to 0 to kill the particle
      timeVarAttr.setY(idx, 0);
    }

    timeVarAttr.needsUpdate = true;
    indices.clear();
    this._dirtyAttachmentSlots.delete(slot);
    this._cleanupAttachmentSlots.delete(slot);
    this._attachmentMatrices.delete(slot);
    this._particleIndicesByAttachmentSlot.delete(slot);
  }

  /**
   * Set the world matrix for a given attachment slot.
   * All particles with this slot will use this matrix for position calculation.
   */
  public setAttachmentMatrix(slot: number, matrix: Matrix4): void {
    if (!this._options.lockToEmitter) {
      return;
    }

    const indices = this._particleIndicesByAttachmentSlot.get(slot);
    if (slot === 0 && (!indices || indices.size === 0)) {
      this._dirtyAttachmentSlots.delete(slot);
      this._cleanupAttachmentSlots.delete(slot);
      this._attachmentMatrices.delete(slot);
      return;
    }

    if (!this._attachmentMatrices.has(slot)) {
      this._attachmentMatrices.set(slot, new Matrix4().copy(matrix));
      this._dirtyAttachmentSlots.add(slot);
      return;
    }

    const target = this._attachmentMatrices.get(slot)!;
    const targetElements = target.elements;
    const sourceElements = matrix.elements;
    let changed = false;

    for (let i = 0; i < 16; i++) {
      if (targetElements[i] !== sourceElements[i]) {
        changed = true;
        break;
      }
    }

    if (changed) {
      target.copy(matrix);
      this._dirtyAttachmentSlots.add(slot);
    }
  }

  /**
   * Get all active attachment slots.
   */
  public getActiveAttachmentSlots(): number[] {
    return Array.from(this._attachmentMatrices.keys());
  }

  /**
   * Sync instance matrices from attachment matrices and prune dead slot indices.
   * Matrix updates are applied only for dirty slots to reduce per-frame CPU work.
   */
  public syncLockedParticleInstanceMatrices(): void {
    if (!this._particleAttachmentSlots || !this._options.lockToEmitter) {
      return;
    }

    if (this._dirtyAttachmentSlots.size === 0 && this._cleanupAttachmentSlots.size === 0) {
      return;
    }

    const geometry = this.mesh.geometry;
    const timeVarAttr = geometry.getAttribute(ATTR_TIME_VAR) as InstancedBufferAttribute;
    let needsUpdate = false;

    for (const slot of this._dirtyAttachmentSlots) {
      if (this._syncAttachmentSlot(slot, timeVarAttr, true)) {
        needsUpdate = true;
      }
    }

    let cleanupProcessed = 0;
    const cleanupIterator = this._cleanupAttachmentSlots.values();
    while (cleanupProcessed < MAX_ATTACHMENT_CLEANUP_SLOTS_PER_FRAME) {
      const next = cleanupIterator.next();
      if (next.done) break;

      const slot = next.value;
      if (this._dirtyAttachmentSlots.has(slot)) {
        continue;
      }

      // Rotate processed slots for fair cleanup across frames.
      this._cleanupAttachmentSlots.delete(slot);
      this._syncAttachmentSlot(slot, timeVarAttr, false);
      if (this._particleIndicesByAttachmentSlot.has(slot)) {
        this._cleanupAttachmentSlots.add(slot);
      }

      cleanupProcessed++;
    }

    this._dirtyAttachmentSlots.clear();

    if (needsUpdate) {
      this._mesh.instanceMatrix.needsUpdate = true;
    }
  }

  private _syncAttachmentSlot(slot: number, timeVarAttr: InstancedBufferAttribute, applyMatrix: boolean): boolean {
    const indices = this._particleIndicesByAttachmentSlot.get(slot);
    if (!indices || indices.size === 0) {
      this._particleIndicesByAttachmentSlot.delete(slot);
      this._dirtyAttachmentSlots.delete(slot);
      this._cleanupAttachmentSlots.delete(slot);
      // Keep matrix while emissions for this slot are queued (e.g. texture not ready).
      // Without this, queued emissions can lose their attachment transform and spawn at emitter origin.
      if (!this._hasPendingEmissionForSlot(slot)) {
        this._attachmentMatrices.delete(slot);
      }
      return false;
    }

    const matrix = applyMatrix ? this._attachmentMatrices.get(slot) : undefined;
    let needsUpdate = false;

    for (const idx of indices) {
      const startTime = timeVarAttr.getX(idx);
      const maxLife = timeVarAttr.getY(idx);
      if (this._currentTime - startTime > maxLife) {
        indices.delete(idx);
        continue;
      }

      if (matrix) {
        this._mesh.setMatrixAt(idx, matrix);
        needsUpdate = true;
      }
    }

    if (indices.size === 0) {
      this._particleIndicesByAttachmentSlot.delete(slot);
      this._dirtyAttachmentSlots.delete(slot);
      this._cleanupAttachmentSlots.delete(slot);
      if (!this._hasPendingEmissionForSlot(slot)) {
        this._attachmentMatrices.delete(slot);
      }
    }

    return needsUpdate;
  }

  private _hasPendingEmissionForSlot(slot: number): boolean {
    for (const pending of this._pendingEmissions.values()) {
      if (pending.attachmentSlot === slot) {
        return true;
      }
    }
    return false;
  }

  private _createPendingEmissionKey(
    attachmentSlot: number,
    emissionOverrides?: ParticleEmitterEmissionOverrides,
  ): string {
    if (!emissionOverrides) {
      return `${attachmentSlot}:0`;
    }

    let overrideId = this._pendingEmissionOverrideIds.get(emissionOverrides as object);
    if (!overrideId) {
      overrideId = this._nextPendingEmissionOverrideId++;
      this._pendingEmissionOverrideIds.set(emissionOverrides as object, overrideId);
    }

    return `${attachmentSlot}:${overrideId}`;
  }

  private _enqueuePendingEmission(
    count: number,
    attachmentSlot: number,
    emissionOverrides?: ParticleEmitterEmissionOverrides,
  ): void {
    const key = this._createPendingEmissionKey(attachmentSlot, emissionOverrides);
    const existing = this._pendingEmissions.get(key);
    if (existing) {
      existing.count += count;
      return;
    }

    if (this._pendingEmissions.size >= MAX_PENDING_EMISSION_GROUPS) {
      return;
    }

    this._pendingEmissions.set(key, {
      count,
      attachmentSlot,
      emissionOverrides,
    });
  }

  private _flushPendingEmissions(): void {
    if (this._pendingEmissions.size === 0) {
      return;
    }

    for (const pending of this._pendingEmissions.values()) {
      this._emit(pending.count, this.mesh.matrixWorld, pending.attachmentSlot, pending.emissionOverrides);
    }

    this._pendingEmissions.clear();
  }

  public dispose(): void {
    this.mesh.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as ParticlesMaterial).dispose();
    this._dirtyAttachmentSlots.clear();
    this._cleanupAttachmentSlots.clear();
    this._attachmentMatrices.clear();
    this._particleIndicesByAttachmentSlot.clear();
    this._pendingEmissions.clear();
  }
}
